import { useCallback, useSyncExternalStore } from "react";

/**
 * How much of the queue an agent can see at once.
 *
 * Density beats generosity here: the brief is six to eight hours a day and fifty to three
 * hundred conversations, and whitespace that reads as elegant in a screenshot reads as
 * scrolling on hour four. But which side of that trade is right is the agent's call, not
 * ours — so it is a mode, not a rewrite. docs/03 §1, docs/08 §6.3.
 *
 * Two states rather than the theme's three: there is no system preference to defer to.
 */
export type Density = "comfortable" | "compact";

const KEY = "mp_density";
const listeners = new Set<() => void>();

function read(): Density {
  try {
    return localStorage.getItem(KEY) === "compact" ? "compact" : "comfortable";
  } catch {
    // Private windows and blocked site data throw on access, not on a missing key.
    return "comfortable";
  }
}

function apply(density: Density): void {
  document.documentElement.setAttribute("data-density", density);
}

export function setDensity(density: Density): void {
  try {
    if (density === "comfortable") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, density);
  } catch {
    // A per-viewer convenience, not state worth failing over.
  }
  apply(density);
  for (const l of listeners) l();
}

/** Call once at startup, before first paint, so rows do not resize under the agent. */
export function initDensity(): void {
  apply(read());
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useDensity(): [Density, (next: Density) => void] {
  const density = useSyncExternalStore(subscribe, read, () => "comfortable" as Density);
  return [density, useCallback(setDensity, [])];
}

export function nextDensity(current: Density): Density {
  return current === "comfortable" ? "compact" : "comfortable";
}
